import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import { events as calloutEvents } from "web/script/components/app-header-callout-classic";
import store from "web/redux/store";
import { watchState } from "web/redux/state-watcher";
import {
    showMainNavigation,
    hideMainNavigation,
    setSelectedGender,
} from "web/redux/ducks/main-navigation";

import "web/script/components/app-header-cart";
import "web/script/components/app-pre-header-bar-classic";
import "web/script/components/app-header-search-classic";

const name = "app-header-main-classic";

export const selectors = {
    GENDER: `.app-header-bar-classic__gender`,
    SEARCH_INPUT_CLASS_NAME: `app-header-search__input`,
};

export const states = {
    GENDER_ACTIVE: `app-header-bar-classic__gender--active`,
    GENDER_SELECTED: `app-header-bar-classic__gender--selected`,
};

const MENU_OPENED = "opened";
const MENU_CLOSED = "closed";

function updateGender(state) {
    return state.mainNavigationReducer.selectedGender;
}

export default baustein.register(name, {
    /**
     * Whether there are any open callouts within the header.
     * Consumed by _handleOpenCallouts.
     * @see _handleOpenCallouts
     * @type {number}
     */
    openCallouts: 0,

    setupEvents(add) {
        const patientMenuToggling = _.throttle(this._toggleMenu.bind(this), 100);

        add("click", selectors.GENDER, patientMenuToggling);
        add("appHeaderMenuClosed", this._onClosedMenu);
        add(calloutEvents.OPENED, this._handleOpenCallouts);
        add(calloutEvents.CLOSED, this._handleOpenCallouts);
        add("searchFocused", this.onSearchFocused);

        watchState(updateGender, (selectedGender) => this._updateGender(selectedGender));
    },

    /**
     * Is the main navigation visible or not
     *
     * @returns {boolean}
     */
    _isMenuOpen() {
        return store.getState().mainNavigationReducer.visible;
    },

    _sendMenuAnalytics(menuState, targetGender) {
        analytics.event("nav", menuState, `nav_${targetGender.toLowerCase()}_menu`);
    },

    /**
     * Toggle the Header menu. Uses redux to show/hide desktop navigation in MainNavigation.jsx
     * When the menu is open, it adds an active state to the gender CTA.
     *
     * @param {Object} event
     * @param {HTMLElement} targetEl
     * @returns {undefined}
     */
    _toggleMenu(event, targetEl) {
        const activeGender = this._getActiveGenderCTA();
        const switchGender = activeGender !== targetEl;
        const targetGender = targetEl.getAttribute("data-target-menu") || "women";

        if (this._isGenderCTA(targetEl)) {
            event.preventDefault();
        }

        if (!switchGender && this._isMenuOpen()) {
            store.dispatch(hideMainNavigation());
            this._sendMenuAnalytics(MENU_CLOSED, targetGender);
        } else {
            store.dispatch(setSelectedGender(targetGender));
            store.dispatch(showMainNavigation());

            this._cleanGenderCTAs();
            targetEl.classList.add(states.GENDER_ACTIVE);
            this._sendMenuAnalytics(MENU_OPENED, targetGender);
        }

        const appHeaderSearch = this.findComponent("app-header-search");
        if (appHeaderSearch) {
            appHeaderSearch.onMenuGenderChange(targetGender);
        }
    },

    _updateGender(selectedGender) {
        if (!selectedGender) {
            return;
        }

        const targetEl = this.el.querySelector(`[data-target-menu=${selectedGender}]`);
        this._cleanGenderCTAs();
        targetEl.classList.add(states.GENDER_ACTIVE);
    },

    /**
     * Generic action of closing the menu and removing any active/selected states
     * from the Gender CTAs.
     *
     * @see _getActiveGenderCTA, _cleanGenderCTAs _onMenuOpen
     * @returns {undefined}
     */
    _onClosedMenu() {
        const activeGenderEl = this._getActiveGenderCTA();

        this._cleanGenderCTAs();

        if (activeGenderEl) {
            activeGenderEl.classList.add(states.GENDER_SELECTED);
        }
    },

    /**
     * Removes any active state from the Gender CTAs.
     *
     * @returns {undefined}
     */
    _cleanGenderCTAs() {
        this.find(selectors.GENDER).forEach((el) => {
            el.classList.remove(states.GENDER_ACTIVE);
            el.classList.remove(states.GENDER_SELECTED);
        });
    },

    /**
     * Gets the current active Gender CTA.
     *
     * @returns {HTMLElement | undefined}
     */
    _getActiveGenderCTA() {
        return this.find(selectors.GENDER).find((el) =>
            el.classList.contains(states.GENDER_ACTIVE)
        );
    },

    /**
     * Asserts whether a supplied element is a Gender CTA or not.
     *
     * @returns {boolean}
     */
    _isGenderCTA(el) {
        return el.classList.contains(selectors.GENDER.substr(1));
    },

    /**
     * Manages when to query or not for Lyst Matte depending on the number of callouts open
     * within the header.
     *
     * @see components/app-header-callout
     * @param {Object} event Baustein's wrapper around DOM native event object.
     * @return {undefined}
     */
    _handleOpenCallouts({ type }) {
        if (type === calloutEvents.OPENED) {
            if (this.openCallouts === 0) {
                this.emit(calloutEvents.OPEN_LYST_MATTE);
            }

            ++this.openCallouts;
        }

        if (type === calloutEvents.CLOSED) {
            if (this.openCallouts === 1) {
                this.emit(calloutEvents.CLOSE_LYST_MATTE);
            }

            --this.openCallouts;
        }
    },

    onSearchFocused() {
        store.dispatch(hideMainNavigation());
    },
});
